import React, { useCallback, useEffect, useState } from "react";
import { FiCheck, FiX } from "react-icons/fi";
import Header from "../components/layout/Header";
import Card from "../components/ui/Card";
import Button from "../components/ui/Button";
import Badge from "../components/ui/Badge";
import Modal from "../components/ui/Modal";
import Select from "../components/ui/Select";
import LoadingSpinner from "../components/ui/LoadingSpinner";
import EmptyState from "../components/ui/EmptyState";
import { leaveApi, LeaveRequest } from "../api/leave";
import { format } from "date-fns";
import { fr } from "date-fns/locale/fr";
import toast from "react-hot-toast";

const STATUS_VARIANT: Record<
  LeaveRequest["status"],
  "warning" | "success" | "danger" | "neutral"
> = {
  pending: "warning",
  approved: "success",
  rejected: "danger",
  cancelled: "neutral",
};

const FILTER_OPTIONS = [
  { value: "", label: "Toutes les demandes" },
  { value: "pending", label: "En attente" },
  { value: "approved", label: "Validées" },
  { value: "rejected", label: "Refusées" },
  { value: "cancelled", label: "Annulées" },
];

const AbsencesRhPage: React.FC = () => {
  const [requests, setRequests] = useState<LeaveRequest[]>([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("pending");
  const [reviewTarget, setReviewTarget] = useState<LeaveRequest | null>(null);
  const [reviewAction, setReviewAction] = useState<"approve" | "reject">(
    "approve",
  );
  const [rhComment, setRhComment] = useState("");
  const [reviewing, setReviewing] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await leaveApi.listRequests({
        status: statusFilter || undefined,
        per_page: 100,
      });
      setRequests(res.data.data?.requests ?? []);
      setPendingCount(res.data.data?.pending_count ?? 0);
    } catch {
      toast.error("Impossible de charger les demandes d'absence");
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const openReview = (request: LeaveRequest, action: "approve" | "reject") => {
    setReviewTarget(request);
    setReviewAction(action);
    setRhComment("");
  };

  const handleReview = async () => {
    if (!reviewTarget) return;
    setReviewing(true);
    try {
      await leaveApi.reviewRequest(reviewTarget.id, {
        action: reviewAction,
        rh_comment: rhComment.trim() || undefined,
      });
      toast.success(
        reviewAction === "approve" ? "Demande validée" : "Demande refusée",
      );
      setReviewTarget(null);
      await loadData();
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message || "Échec du traitement";
      toast.error(message);
    } finally {
      setReviewing(false);
    }
  };

  return (
    <div>
      <Header
        title="Absences"
        subtitle="Validez ou refusez les demandes de congés de vos collaborateurs"
      />

      <div style={{ padding: "2rem", maxWidth: 1100, margin: "0 auto" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "1rem",
            flexWrap: "wrap",
            gap: "0.75rem",
          }}
        >
          <div
            style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}
          >
            {pendingCount > 0 && (
              <Badge variant="warning">{pendingCount} en attente</Badge>
            )}
          </div>
          <div style={{ minWidth: 220 }}>
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              options={FILTER_OPTIONS}
            />
          </div>
        </div>

        {loading ? (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              padding: "3rem",
            }}
          >
            <LoadingSpinner size={36} />
          </div>
        ) : requests.length === 0 ? (
          <Card padding="2rem">
            <EmptyState
              title="Aucune demande"
              description={
                statusFilter === "pending"
                  ? "Aucune demande en attente de validation."
                  : "Aucune demande ne correspond à ce filtre."
              }
            />
          </Card>
        ) : (
          <Card padding="0">
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1.5fr 1.5fr 1fr 100px 140px",
                gap: "0.5rem",
                padding: "0.75rem 1.25rem",
                background: "var(--color-bg)",
                fontSize: "var(--font-size-xs)",
                fontWeight: 600,
                color: "var(--color-text-secondary)",
              }}
            >
              <span>Collaborateur</span>
              <span>Absence</span>
              <span>Durée</span>
              <span>Statut</span>
              <span style={{ textAlign: "right" }}>Actions</span>
            </div>
            {requests.map((req, index) => (
              <div
                key={req.id}
                style={{
                  display: "grid",
                  gridTemplateColumns: "1.5fr 1.5fr 1fr 100px 140px",
                  gap: "0.5rem",
                  alignItems: "center",
                  padding: "1rem 1.25rem",
                  borderTop:
                    index > 0
                      ? "1px solid var(--color-border-light)"
                      : undefined,
                }}
              >
                <div>
                  <p
                    style={{
                      margin: 0,
                      fontWeight: 500,
                      fontSize: "var(--font-size-sm)",
                    }}
                  >
                    {req.employee_name || req.user_name || "-"}
                  </p>
                  {req.user_email && (
                    <p
                      style={{
                        margin: 0,
                        fontSize: "var(--font-size-xs)",
                        color: "var(--color-text-muted)",
                      }}
                    >
                      {req.user_email}
                    </p>
                  )}
                </div>
                <div>
                  <p style={{ margin: 0, fontSize: "var(--font-size-sm)" }}>
                    {req.leave_type_label}
                  </p>
                  <p
                    style={{
                      margin: "0.15rem 0 0",
                      fontSize: "var(--font-size-xs)",
                      color: "var(--color-text-muted)",
                    }}
                  >
                    {format(new Date(req.start_date), "d MMM yyyy", {
                      locale: fr,
                    })}
                    {" → "}
                    {format(new Date(req.end_date), "d MMM yyyy", {
                      locale: fr,
                    })}
                  </p>
                  {req.comment && (
                    <p
                      style={{
                        margin: "0.25rem 0 0",
                        fontSize: "var(--font-size-xs)",
                        color: "var(--color-text-secondary)",
                      }}
                    >
                      « {req.comment} »
                    </p>
                  )}
                </div>
                <span style={{ fontSize: "var(--font-size-sm)" }}>
                  {req.days_count} j.
                  {req.account_label ? (
                    <span
                      style={{
                        display: "block",
                        fontSize: "var(--font-size-xs)",
                        color: "var(--color-text-muted)",
                      }}
                    >
                      {req.account_label}
                    </span>
                  ) : null}
                </span>
                <Badge variant={STATUS_VARIANT[req.status]} size="sm">
                  {req.status_label}
                </Badge>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: "0.35rem",
                  }}
                >
                  {req.status === "pending" ? (
                    <>
                      <button
                        type="button"
                        title="Valider"
                        onClick={() => openReview(req, "approve")}
                        style={{
                          background: "none",
                          border: "none",
                          color: "var(--color-success)",
                          cursor: "pointer",
                          padding: "0.25rem",
                        }}
                      >
                        <FiCheck size={18} />
                      </button>
                      <button
                        type="button"
                        title="Refuser"
                        onClick={() => openReview(req, "reject")}
                        style={{
                          background: "none",
                          border: "none",
                          color: "var(--color-danger)",
                          cursor: "pointer",
                          padding: "0.25rem",
                        }}
                      >
                        <FiX size={18} />
                      </button>
                    </>
                  ) : (
                    <span
                      style={{
                        fontSize: "var(--font-size-xs)",
                        color: "var(--color-text-muted)",
                      }}
                    >
                      {" "}
                      -{" "}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </Card>
        )}
      </div>

      <Modal
        isOpen={!!reviewTarget}
        onClose={() => setReviewTarget(null)}
        title={
          reviewAction === "approve"
            ? "Valider la demande"
            : "Refuser la demande"
        }
        footer={
          <>
            <Button variant="outline" onClick={() => setReviewTarget(null)}>
              Annuler
            </Button>
            <Button
              onClick={handleReview}
              disabled={reviewing}
              style={
                reviewAction === "reject"
                  ? { background: "var(--color-danger)" }
                  : undefined
              }
            >
              {reviewing
                ? "Traitement…"
                : reviewAction === "approve"
                  ? "Valider"
                  : "Refuser"}
            </Button>
          </>
        }
      >
        {reviewTarget && (
          <div
            style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}
          >
            <p style={{ margin: 0, fontSize: "var(--font-size-sm)" }}>
              <strong>
                {reviewTarget.employee_name || reviewTarget.user_name}
              </strong>
              {" - "}
              {reviewTarget.leave_type_label}
              {" ("}
              {reviewTarget.days_count} j.)
            </p>
            <div>
              <label
                style={{
                  display: "block",
                  marginBottom: "0.35rem",
                  fontSize: "var(--font-size-sm)",
                  fontWeight: 500,
                }}
              >
                Commentaire RH (optionnel)
              </label>
              <textarea
                value={rhComment}
                onChange={(e) => setRhComment(e.target.value)}
                rows={3}
                style={{
                  width: "100%",
                  padding: "0.625rem 0.875rem",
                  fontSize: "var(--font-size-sm)",
                  border: "1px solid var(--color-border)",
                  borderRadius: "var(--radius-md)",
                  fontFamily: "var(--font-family)",
                  resize: "vertical",
                }}
                placeholder={
                  reviewAction === "reject"
                    ? "Motif du refus…"
                    : "Message pour le collaborateur…"
                }
              />
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default AbsencesRhPage;
